/**
 * Where a /join creator goes after the cards, and how they get there (Tad, 2026-11-03).
 *
 * /join has no account screen and starts no agent run. One login lives at /login, and one
 * destination is decided HERE, where the run state is known: a creator with cards on the
 * insights path goes to their feed, everyone else to Creator Studio. The destination rides
 * through /login in `redirectTo`, so no later route has to re-read the run state.
 *
 * ⚠️ The Creator Studio destination carries `src=join`. That is what tells /login this
 * return may claim the link's handle; without it /login treats a
 * `/creator_studio?creator_id=` return as the legacy TikTok account-link leg
 * (`lib/auth-redirect.ts` → `getCreatorIdFromRedirectTo`).
 */

export const CREATOR_FEED_PATH = "/insights";
export const CREATOR_STUDIO_PATH = "/creator_studio";

/** Marks a Creator Studio return that came from /join. */
export const JOIN_RETURN_SRC = "join";

/**
 * Rides on the signed-out exit's return to /insights: this creator has just finished the
 * initial cards (2026-11-04). The /insights loader treats a linked creator carrying it as
 * having seen them, so a lost `join-cards-seen` write never replays the cards straight
 * after login. Unsigned, like `creator_id`: a forged one only skips someone's intro.
 */
export const JOIN_CARDS_SEEN_PARAM = "cards_seen";

export type JoinAuthMode = "signin" | "signup";

/** The /join exit: the feed when there are cards, Creator Studio when there are none. */
export function joinDestination(creatorId: string, hasAgentRun: boolean): string {
  const base = hasAgentRun ? CREATOR_FEED_PATH : CREATOR_STUDIO_PATH;
  const params = new URLSearchParams({ creator_id: creatorId });
  if (!hasAgentRun) params.set("src", JOIN_RETURN_SRC);
  return `${base}?${params.toString()}`;
}

/**
 * The one login screen, carrying the destination it must come back to. Every exit from the
 * cards passes `cardsSeen` — the cards' own exit (C02) and the loader's exits alike — so the
 * feed return is marked (JOIN_CARDS_SEEN_PARAM) and the creator always lands on the next
 * insight card, never back on the cards.
 */
export function joinLoginHref(
  creatorId: string,
  hasAgentRun: boolean,
  mode: JoinAuthMode,
  options: { cardsSeen?: boolean } = {}
): string {
  let destination = joinDestination(creatorId, hasAgentRun);
  if (options.cardsSeen && hasAgentRun) destination += `&${JOIN_CARDS_SEEN_PARAM}=1`;
  return `/login?redirectTo=${encodeURIComponent(destination)}&mode=${mode}`;
}

/** Sign in when the creator already holds an account, create one otherwise. */
export function joinAuthMode(hasExistingAccount: boolean): JoinAuthMode {
  return hasExistingAccount ? "signin" : "signup";
}
